/**
 * Tail buffer for the live code preview (see CodeStreamSnippet).
 *
 * The server streams the code being generated as small deltas that may cut a
 * line anywhere. The snippet only ever shows the last few lines, so this keeps
 * exactly that: glue the delta onto the current line, push the rest, drop what
 * scrolled out. Pure — never mutates its input — so it can run inside a React
 * state updater.
 *
 * In its own module rather than next to the component because the lint runs
 * with react-refresh/only-export-components and --max-warnings 0.
 */

/** Rows the snippet shows. At text-mono-sm (12px / 1.5) plus py-1.5 that is 120px. */
export const CODE_STREAM_MAX_LINES = 6

/**
 * Longest line kept. The snippet is `whitespace-pre overflow-hidden`, so only
 * the head is ever visible; this just bounds memory against a runaway line.
 */
export const CODE_STREAM_MAX_LINE_CHARS = 400

/**
 * Append a streamed delta to a tail buffer.
 *
 * A delta ending in "\n" leaves a trailing "" — the line the caret sits on.
 * An empty delta returns the same array, so React can skip the re-render.
 */
export function appendCodeTail(
  lines: string[],
  delta: string,
  maxLines: number = CODE_STREAM_MAX_LINES,
  maxLineChars: number = CODE_STREAM_MAX_LINE_CHARS,
): string[] {
  const text = delta.replace(/\r\n?/g, '\n')
  if (text === '') return lines

  const parts = text.split('\n')
  const next = lines.length > 0 ? [...lines] : ['']

  next[next.length - 1] = clip(next[next.length - 1] + parts[0], maxLineChars)
  for (let i = 1; i < parts.length; i++) {
    next.push(clip(parts[i], maxLineChars))
  }

  return next.length > maxLines ? next.slice(-maxLines) : next
}

function clip(line: string, max: number): string {
  return line.length > max ? line.slice(0, max) : line
}
